import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Wrench, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CAMPOS = [
  { k: "instrucciones_instalacion", label: "Instrucciones de instalación", rows: 5 },
  { k: "herramientas", label: "Herramientas y consumibles", rows: 4 },
  { k: "seguridad_epis", label: "Seguridad y EPIs", rows: 4 },
];

export default function MemoriaMontajeDialog({ peticionId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [mem, setMem] = useState(null);
  const [notas, setNotas] = useState("");
  const [generando, setGenerando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [modoRegenerar, setModoRegenerar] = useState(false);

  useEffect(() => {
    api.get(`/peticiones/${peticionId}/memoria-montaje`)
      .then((r) => setMem(r.data.actual))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [peticionId]);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post(`/peticiones/${peticionId}/memoria-montaje/generar`, { notas_extra: notas });
      setMem(data);
      setModoRegenerar(false);
      toast.success("Instrucciones generadas. Revísalas y descarga el PDF.");
    } catch (e) { toast.error(e.response?.data?.detail || "Error generando (reintenta)"); }
    finally { setGenerando(false); }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.patch(`/peticiones/${peticionId}/memoria-montaje`, { secciones: mem.secciones });
      toast.success("Guardado");
    } catch (e) { toast.error("Error guardando"); }
    finally { setGuardando(false); }
  };

  const descargarPdf = async () => {
    try {
      const r = await api.get(`/peticiones/${peticionId}/memoria-montaje/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Error generando el PDF"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[95vw] sm:w-auto max-w-[640px] max-h-[88vh] overflow-y-auto p-4"
        data-testid="memoria-montaje-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-400" /> Memoria de montaje
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Orden de trabajo para instaladores: puntos, componentes con QR y código corto, medidas y direcciones salen directos de la petición. La IA solo redacta las instrucciones. Sin datos económicos.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div>
        ) : generando ? (
          <div className="py-10 text-center space-y-2" data-testid="montaje-generando">
            <Loader2 className="w-7 h-7 animate-spin mx-auto text-amber-400" />
            <div className="text-sm text-zinc-300">Redactando instrucciones con IA...</div>
            <div className="text-xs text-zinc-500">15-40 segundos. No cierres la ventana.</div>
          </div>
        ) : (!mem || modoRegenerar) ? (
          <div className="space-y-3" data-testid="montaje-precheck">
            <div>
              <div className="text-xs text-zinc-400 mb-1">Indicaciones para la IA (opcional)</div>
              <Textarea rows={3} value={notas} onChange={(e) => setNotas(e.target.value)}
                placeholder="Ej: escaparates en altura, se necesita escalera; el centro solo permite trabajar antes de las 10h..."
                data-testid="montaje-notas" className="bg-zinc-900 border-zinc-800 text-sm" />
            </div>
            <div className="flex gap-2">
              {modoRegenerar && (
                <Button variant="outline" onClick={() => setModoRegenerar(false)}
                  className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              )}
              <Button onClick={generar} data-testid="montaje-generar-btn"
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
                ✨ Generar instrucciones con IA
              </Button>
              <Button variant="outline" onClick={descargarPdf} data-testid="montaje-pdf-sin-ia-btn"
                title="El PDF con los puntos, QRs y componentes funciona también sin instrucciones IA"
                className="bg-transparent border-zinc-700 text-zinc-200">
                <Download className="w-3.5 h-3.5 mr-1" /> PDF sin IA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="montaje-editor">
            {CAMPOS.map((c) => (
              <div key={c.k}>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">{c.label}</div>
                <Textarea rows={c.rows} value={mem.secciones?.[c.k] || ""}
                  onChange={(e) => setMem((m) => ({ ...m, secciones: { ...m.secciones, [c.k]: e.target.value } }))}
                  data-testid={`montaje-seccion-${c.k}`}
                  className="bg-zinc-900 border-zinc-800 text-xs leading-relaxed" />
              </div>
            ))}
            <div className="text-[10px] text-zinc-500">Generada por {mem.generada_por} · las líneas "- " salen como viñetas en el PDF</div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={guardar} disabled={guardando} data-testid="montaje-guardar-btn"
                className="bg-transparent border-zinc-700 text-zinc-200">
                {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "💾 Guardar"}
              </Button>
              <Button variant="outline" onClick={() => setModoRegenerar(true)} data-testid="montaje-regenerar-btn"
                className="bg-transparent border-zinc-700 text-zinc-400">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerar
              </Button>
              <Button onClick={async () => { await guardar(); descargarPdf(); }} data-testid="montaje-pdf-btn"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
